import { INoteEntity } from '@maia/shared';
import { getErrors, SideDrawer, SidedrawerProps } from '@maia/ui';

import { useAppForm } from '../../form/dynamic-form/dynamic-form';
import * as v from 'valibot';
import { useDrawerStore } from './../../../stores/drawer.store';

type FormValues = {
  type: INoteEntity['type'];
  subject: string;
  explanation: {
    markdown: string;
    files: Array<File>;
  };
};

type Props = {
  onSubmit: (values: FormValues) => Promise<void>;
} & Pick<SidedrawerProps, 'onOpenChange' | 'portalRef'>;

export function AddNoteSidedrawer(props: Props) {
  const { portalRef, onSubmit, ...rest } = props;
  const { state, closeDrawer } = useDrawerStore();

  const form = useAppForm({
    defaultValues: {
      type: 'mail_correspondence',
      subject: '',
      explanation: {
        markdown: '',
        files: [],
      },
    } as {
      type: INoteEntity['type'];
      subject: string;
      explanation: {
        markdown: string;
        files: Array<File>;
      };
    },
    onSubmit: async ({ value }) => await onSubmit(value),
  });

  return (
    <SideDrawer
      portalRef={portalRef}
      title="Notitie toevoegen"
      className="w-full max-w-[var(--max-side-drawer-width)]"
      isOpen={state?.isOpen && state.info?.type === 'note.add'}
      onOpenChange={closeDrawer}
      {...rest}
    >
      <form
        id="more-information-form"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
        className="grid grid-cols-12 gap-24 col-span-full"
      >
        <div className="col-span-full">
          <form.AppField
            name="type"
            validators={{
              onChange: v.union([
                v.literal('phone_correspondence'),
                v.literal('mail_correspondence'),
                v.literal('comment'),
                v.literal('question'),
              ]),
            }}
          >
            {(field) => {
              return (
                <field.RadioField
                  label="Type"
                  options={[
                    {
                      label: 'Telefoon',
                      value: 'phone_correspondence',
                    },
                    {
                      label: 'Mail',
                      value: 'mail_correspondence',
                    },
                    { label: 'Commentaar', value: 'comment' },
                    { label: 'Vraag', value: 'question' },
                  ]}
                  error={getErrors(field)}
                />
              );
            }}
          </form.AppField>
        </div>

        <div className="col-span-full">
          <form.AppField
            name="subject"
            validators={{
              onMount: v.pipe(
                v.string('Onderwerp is verplicht'),
                v.trim(),
                v.minLength(1, 'Onderwerp is verplicht'),
              ),
              onChange: v.pipe(
                v.string('Onderwerp is verplicht'),
                v.trim(),
                v.minLength(1, 'Onderwerp is verplicht'),
              ),
            }}
          >
            {(field) => {
              return (
                <field.TextField label="Onderwerp" error={getErrors(field)} />
              );
            }}
          </form.AppField>
        </div>

        <div className="col-span-full">
          <form.Subscribe
            selector={(state) => [
              state.canSubmit,
              state.isSubmitting,
              state.isValid,
              state.isDirty,
            ]}
          >
            {([canSubmit, isSubmitting, isValid, isDirty]) => (
              <form.AppField
                name="explanation"
                validators={{
                  onChange: v.object({
                    markdown: v.pipe(
                      v.string('Toelichting is verplicht'),
                      v.trim(),
                      v.minLength(1, 'Toelichting is verplicht'),
                    ),
                    files: v.array(v.file()),
                  }),
                }}
              >
                {(field) => {
                  return (
                    <field.RichtTextEditorField
                      label="Toelichting"
                      error={getErrors(field)}
                      isDisabled={
                        !isValid || !canSubmit || isSubmitting || !isDirty
                      }
                    />
                  );
                }}
              </form.AppField>
            )}
          </form.Subscribe>
        </div>
      </form>
    </SideDrawer>
  );
}
